/**
 * FERP Portal - My Profile Page (Layer 1 - Employee View)
 *
 * Shows current user info from auth context.
 * Displays tenant info and user's role/capabilities.
 *
 * FERP Principles:
 * - Dumb Frontend: All data from AuthContext (backend-provided via OIDC)
 * - Data-driven: Roles, tenant info from backend tokens
 * - NO hardcoded values: SafetyLevel badge from tenant config
 */

import {
  Container, Title, Text, Card, Group, Stack, Badge,
  ThemeIcon, Alert, Divider, Paper,
} from '@mantine/core';
import {
  IconUser, IconAlertTriangle, IconBuilding, IconShield,
  IconMail, IconId, IconUsers, IconKey,
} from '@tabler/icons-react';
import { useAuth } from '@/auth';

export function MyProfilePage() {
  const { user, tenant, viewLayer, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="Not Signed In">
          Please sign in to view your profile.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2}>My Profile</Title>
          <Text c="dimmed" size="sm">Your account information and settings</Text>
        </div>

        {/* User Info Card */}
        <Card withBorder p="lg">
          <Stack gap="md">
            <Group gap="md">
              <ThemeIcon variant="light" size="xl" radius="xl">
                <IconUser size={24} />
              </ThemeIcon>
              <div>
                <Text size="lg" fw={700}>{user.displayName}</Text>
                <Text size="sm" c="dimmed">{user.email}</Text>
              </div>
            </Group>

            <Divider />

            {/* Details */}
            <Stack gap="sm">
              <Group gap="xs">
                <IconId size={16} color="gray" />
                <Text size="sm" c="dimmed" w={120}>User ID</Text>
                <Text size="sm" ff="monospace">{user.id}</Text>
              </Group>

              <Group gap="xs">
                <IconMail size={16} color="gray" />
                <Text size="sm" c="dimmed" w={120}>Email</Text>
                <Text size="sm">{user.email}</Text>
              </Group>

              <Group gap="xs">
                <IconUsers size={16} color="gray" />
                <Text size="sm" c="dimmed" w={120}>Roles</Text>
                <Group gap={4}>
                  {user.roles.map((role: string) => (
                    <Badge key={role} size="sm" variant="light">{role}</Badge>
                  ))}
                </Group>
              </Group>

              <Group gap="xs">
                <IconKey size={16} color="gray" />
                <Text size="sm" c="dimmed" w={120}>View Layer</Text>
                <Badge size="sm" color={
                  viewLayer === 'architect' ? 'grape' :
                  viewLayer === 'admin' ? 'blue' : 'green'
                }>{viewLayer}</Badge>
              </Group>
            </Stack>
          </Stack>
        </Card>

        {/* Tenant Info Card */}
        {tenant && (
          <Card withBorder p="lg">
            <Stack gap="md">
              <Group gap="md">
                <ThemeIcon variant="light" size="xl" radius="xl" color="blue">
                  <IconBuilding size={24} />
                </ThemeIcon>
                <div>
                  <Text size="lg" fw={700}>{tenant.name}</Text>
                  <Text size="sm" c="dimmed">Organization</Text>
                </div>
              </Group>

              <Divider />

              <Stack gap="sm">
                <Group gap="xs">
                  <IconId size={16} color="gray" />
                  <Text size="sm" c="dimmed" w={120}>Tenant ID</Text>
                  <Text size="sm" ff="monospace">{tenant.id}</Text>
                </Group>

                <Group gap="xs">
                  <IconShield size={16} color="gray" />
                  <Text size="sm" c="dimmed" w={120}>Safety Level</Text>
                  <Badge size="sm" color={
                    tenant.safetyLevel === 'CRITICAL' ? 'red' :
                    tenant.safetyLevel === 'HIGH' ? 'orange' :
                    tenant.safetyLevel === 'MEDIUM' ? 'yellow' : 'green'
                  }>{tenant.safetyLevel}</Badge>
                </Group>
              </Stack>
            </Stack>
          </Card>
        )}

        {/* Account Actions */}
        <Paper withBorder p="md">
          <Stack gap="xs">
            <Text size="sm" fw={500}>Account Settings</Text>
            <Text size="xs" c="dimmed">
              Profile settings, password changes, and notification preferences are managed
              through your organization&apos;s identity provider (Authentik).
            </Text>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}

export default MyProfilePage;
